import { PermissionAwareInput, PermissionAwareTextarea } from '../../../contexts/PermissionAccessContext';
import { ReadOnly } from './safetyDrawerShared';
import { inputClass, str } from './unitFields';
import { RATING_MAX, RATING_MIN, meanRating, norm } from './tableForm';

/**
 * Survey elements (the `survey` table's unmanaged child): one rating (0–10, one decimal)
 * + comments row per active category of the survey's type, with the overall rating shown
 * read-only — the server stores the 1-decimal mean, so the preview is computed the same way.
 */

export interface ElementsEditorProps {
  rows: Record<string, unknown>[];
  onChange: (rows: Record<string, unknown>[]) => void;
  readOnly: boolean;
  /** The stored `surveyrating`, shown when there are no element rows. */
  storedRating: unknown;
}

const th = 'px-1.5 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted';

export default function ElementsEditor({ rows, onChange, readOnly, storedRating }: ElementsEditorProps) {
  const set = (i: number, patch: Record<string, unknown>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <ReadOnly label="Overall Rating" value={rows.length ? meanRating(rows) : str(storedRating)} />
      </div>
      {rows.length === 0 ? (
        <p className="py-3 text-center text-sm text-muted">No survey categories for this survey type.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={th}>Category</th>
              <th className={th}>Rating (0–10)</th>
              <th className={th}>Comments</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const cat = norm(r.category) || `Category ${i + 1}`;
              return (
                <tr key={`${r.surveycategoryrecno}-${i}`}>
                  <td className="px-1.5 py-1 align-top font-medium">{cat}</td>
                  <td className="px-1 py-0.5 align-top">
                    <PermissionAwareInput type="number" min={RATING_MIN} max={RATING_MAX} step="0.1" inputMode="decimal" aria-label={`${cat} rating`}
                      className={`${inputClass} w-24`} value={str(r.elementrating)} disabled={readOnly}
                      onChange={(e) => set(i, { elementrating: e.target.value })} />
                  </td>
                  <td className="px-1 py-0.5 align-top">
                    <PermissionAwareTextarea rows={2} aria-label={`${cat} comments`} className={inputClass} value={str(r.elementcomments)} disabled={readOnly}
                      onChange={(e) => set(i, { elementcomments: e.target.value })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
